import React, { useState, useEffect } from "react";
import { Button, Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useAlert } from "../../components/Alert";
import instance from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useParams } from "react-router-dom";

const useStyles = makeStyles({
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "400px",
    margin: "0 auto",
    padding: "16px",
  },
  submitButton: {
    marginTop: "16px",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "8px",
    maxWidth: "400px",
    width: "90%",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  modalTitle: {
    marginBottom: "16px",
    fontWeight: "bold",
  },
  paymentDetail: {
    margin: "8px 0",
  },
  modalButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    justifyContent: "flex-end",
  },
  downloadButton: {
    marginTop: "24px",
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
});

const ViewInvoice = () => {
  const classes = useStyles();
  const { showAlert } = useAlert();
  const { invoiceId } = useParams();

  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceData(invoiceId);
    } else {
      showAlert("Invoice ID not found in URL", "error");
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    let intervalId;

    // Set up polling if invoice data exists but invoice file is not available
    if (invoiceData && !invoiceData.invoice && invoiceId) {
      intervalId = setInterval(() => {
        fetchInvoiceData(invoiceId, true);
      }, 3000);
    }

    // Cleanup interval on component unmount or when dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [invoiceData?.invoice, invoiceId]);

  const fetchInvoiceData = async (invoiceId, isPolling = false) => {
    try {
      if (!isPolling) {
        setLoading(true);
      }
      const response = await instance.get(`/payments/invoice/${invoiceId}/`);
      setInvoiceData(response.data);
    } catch (error) {
      showAlert("Failed to fetch invoice data", "error");
      console.error("Error fetching invoice:", error);
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  };

  const handleDownloadInvoice = () => {
    if (invoiceData?.invoice) {
      window.open(invoiceData.invoice, "_blank");
    } else {
      showAlert(
        "error",
        "Invoice file not yet generated. Please try again later."
      );
    }
  };

  if (loading) {
    return (
      <>
        <CustomAppBar />
        <Box className={classes.formContainer}>
          <Typography>Loading invoice...</Typography>
        </Box>
      </>
    );
  }

  if (!invoiceData) {
    return (
      <>
        <CustomAppBar />
        <Box className={classes.formContainer}>
          <Typography>Invoice not found</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Box className={classes.formContainer}>
        <Typography
          variant="h5"
          style={{ marginBottom: "24px", textAlign: "center" }}
        >
          Invoice Details
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Invoice Number:</strong> {invoiceData.invoice_number}
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Invoice Type:</strong> {invoiceData.invoice_type}
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Amount:</strong> ₹{invoiceData.amount}
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>GST Amount:</strong> ₹{invoiceData.gst_amount}
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Total Amount:</strong> ₹
          {(
            parseFloat(invoiceData.amount) + parseFloat(invoiceData.gst_amount)
          ).toFixed(2)}
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Created By:</strong> {invoiceData.created_by_name} (ID:{" "}
          {invoiceData.created_by})
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Recipient:</strong> {invoiceData.recipient_name} (ID:{" "}
          {invoiceData.recipient})
        </Typography>

        <Typography className={classes.paymentDetail}>
          <strong>Created At:</strong>{" "}
          {new Date(invoiceData.created_at).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>

        {/* Download Button */}
        <Button
          variant="contained"
          className={classes.downloadButton}
          onClick={handleDownloadInvoice}
          fullWidth
        >
          Download Invoice
        </Button>
      </Box>
    </>
  );
};

export default ViewInvoice;
